import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import makeStyles from "@material-ui/styles/makeStyles";
import React from "react";
import { SwissGermanLocale } from "../../../../data/constants";
import { ModuleReference } from "../../../../model/CertificationDataContextModel";
import { ModuleSelectionViewModel } from "../../../../model/ExamSignup/ModuleSelectionViewModel";
import TranslatedMessage from "../../../translations/TranslatedMessage";
import { useSignUpContext } from "../../CertificationDataWizardContext/CertificationDataWizardContext";
import SummaryModuleRow from "./SummaryModuleRow";
import Alert from "@material-ui/lab/Alert";

const useStyles = makeStyles(() => ({
  skipRow: {
    padding: "1px",
  },
}));

type ModuleData = {
  selection: ModuleSelectionViewModel;
  reference: ModuleReference;
};

type SelectionSummaryProps = {
  onRemoveModule: (module: ModuleSelectionViewModel) => void;
  onPayModule: (module: ModuleSelectionViewModel) => void;
  onPaymentError: () => void;
  isLoading: boolean;
};

const SelectionSummary: React.FunctionComponent<SelectionSummaryProps> = ({
  onRemoveModule,
  onPayModule,
  onPaymentError,
  isLoading,
}: SelectionSummaryProps) => {
  const {
    referenceModules,
    data: { modulesSelection },
  } = useSignUpContext();

  const classes = useStyles();

  const moduleRows = modulesSelection
    .map((m: ModuleSelectionViewModel) => {
      const refModule = referenceModules.find(
        (r) => r.examSessionId === m.examSessionId && r.courseId === m.courseId && r.id === m.moduleId
      );
      if (!refModule) {
        return undefined;
      }
      return { selection: m, reference: refModule } as ModuleData;
    })
    .filter((r): r is ModuleData => r !== undefined);

  const totalPrice = moduleRows.reduce((sum, row) => sum + row.reference.price, 0);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography>
          <TranslatedMessage id="startCertification.paymentAndCompletionStep.summary" />
        </Typography>
        <Alert severity="warning">
          <strong>
            <TranslatedMessage id="startCertification.paymentAndCompletionStep.hint" />
          </strong>
        </Alert>
        <TableContainer component={Paper} style={{ marginTop: "0.5rem", marginBottom: "1rem" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.moduleColumn" />
                </TableCell>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.examColumn" />
                </TableCell>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.examTypeColumn" />
                </TableCell>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.locationColumn" />
                </TableCell>
                <TableCell>
                  <TranslatedMessage id="application.examLocationAndDate.dateColumn" />
                </TableCell>
                <TableCell align="right" width={30}>
                  <TranslatedMessage id="startCertification.moduleSelectionStep.priceColumn" />
                </TableCell>
                <TableCell align="center">
                  <TranslatedMessage id="startCertification.paymentAndCompletionStep.actionColumn" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={8} className={classes.skipRow} />
              </TableRow>
              {modulesSelection.map((module, index) => (
                <React.Fragment key={index}>
                  <SummaryModuleRow
                    module={module}
                    onRemoveModule={onRemoveModule}
                    onPayModule={onPayModule}
                    isLoading={isLoading}
                    onPaymentError={onPaymentError}
                  />
                  <TableRow>
                    <TableCell colSpan={8} className={classes.skipRow} />
                  </TableRow>
                </React.Fragment>
              ))}
              <TableRow>
                <TableCell align="right" colSpan={6}>
                  {totalPrice.toLocaleString(SwissGermanLocale, { minimumFractionDigits: 2 })}
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};

export default SelectionSummary;
